<template>
  <div class="bh-user-general">
    <v-container>
      <v-row>
        <v-col cols="12" md="6">
          <component
            class="bh-user-general-field"
            :class="`bh-user-general-field-${field.component}`"
            :is="field.component"
            v-for="field in fields"
            :label="field.label"
            :key="field.name"
            :value="bhUser[field.name]"
            :id="field.name"
            :items="field.items"
            :type="field.type"
            :minNumber="field.minNumber"
            :maxNumber="field.maxNumber"
            :placeholder="field.placeholder"
            :confirmationPopup="field.confirmationPopup"
            inputEditingBackgroundClass="bhBlue4"
            showConfirmEdit
            :validate="field.validate"
            :validationType="field.validationType"
            :condition="field.condition"
            :disabled="field.disabled"
            @input="inputUpdate(field.name, $event)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="d-flex align-center justify-end">
          <v-btn
            v-if="isPermitted && !bhUser.isActive"
            text
            color="bhRedDark"
            class="rounded-pill mr-2"
            elevation="0"
            @click="promptDeleteUser"
          >
            Delete user
          </v-btn>
          <v-btn
            class="rounded-pill"
            text
            color="bhRedDark"
            elevation="0"
            @click="promptActivateState"
            v-if="isRoleAllowed"
          >
            {{ bhUserStatusText }} user
          </v-btn>
          <v-btn
            color="accent"
            class="rounded-pill"
            elevation="0"
            @click="confirmationPopupWelcomeMessage = true"
            v-if="isRoleAllowed"
          >
            Send welcome e-mail
          </v-btn>
        </v-col>
      </v-row>
      <remove-user-dialog
        :show-dialog="confirmationPopupDelete"
        :delete-title="
          'This action will remove this user from app. This action is irreversible! Proceed with caution.'
        "
        :delete-button-title="'Remove user'"
        @confirmDeleteUser="deleteBhUser"
        @close-dialog="confirmationPopupDelete = false"
      />
      <v-dialog
        v-model="confirmationPopupActiveStatus"
        persistent
        max-width="290"
        @click:outside="onClickOutsideConfirmationActive"
        @keydown="onKeydownConfirmationActive"
      >
        <v-card>
          <v-card-title class="headline"
            >{{ bhUserStatusText }} user</v-card-title
          >
          <v-card-text>Are you sure?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeConfirmationActive"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                closeConfirmationActive()
                toggleActiveState()
              "
              >{{ bhUserStatusText }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="confirmationPopupWelcomeMessage"
        persistent
        max-width="290"
        @click:outside="onClickOutsideWelcome"
        @keydown="onKeydownWelcome"
      >
        <v-card>
          <v-card-title class="headline">Welcome email</v-card-title>
          <v-card-text
            >Do you want to send a message to the user with all access
            information?</v-card-text
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeConfirmationWelcome"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                closeConfirmationWelcome()
                prepareSendNewUserEmail()
              "
              >Ok</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <deactivation-dialog
        :show-dialog="showDeactivationDialog"
        :user="bhUser"
        :participants="bhUserPatients.participants"
        :participantsPagination="pagination"
        :consultants="computedConsultants"
        :currentConsultant="bhUser"
        :reloader="deactivationDialogReloader"
        @onInput="changeConsultant"
        @onSave="toggleActiveState"
        @close-dialog="closeBhUserDeactivationDialog"
      />
    </v-container>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import phoneFormat from '@/helpers/phoneFormat.js'
import BhSelect from '@/components/inputs/BhSelect.vue'
import DeactivationDialog from '@/components/dialogs/BhUserDeactivationDialog.vue'
import RemoveUserDialog from '@/components/dialogs/RemoveUserDialog.vue'

export default {
  name: 'BhUserGeneral',
  components: {
    BhTextInput,
    BhSelect,
    DeactivationDialog,
    RemoveUserDialog,
  },
  data() {
    return {
      confirmationPopupActiveStatus: false,
      confirmationPopupWelcomeMessage: false,
      confirmationPopupDelete: false,
      showDeactivationDialog: false,
      consultants: [],
      deactivationDialogReloader: 0,
    }
  },
  watch: {
    async bhUser(user) {
      await this.getBhUsers(this.bhUsersQuery)

      this.consultants = this.bhUsers || []
    },
  },
  computed: {
    ...mapState('bhUsersStates', [
      'bhUser',
      'bhUsers',
      'bhUserPatients',
      'pagination',
    ]),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    userId() {
      return this.$route.params.id
    },
    bhUserStatusText() {
      return this.bhUser.isActive ? 'Deactivate' : 'Activate'
    },
    consultantsQueryParams() {
      return {
        roleId: this.dictionaryBhUsersRoleIds.Consultant,
        isActive: true,
        sortBy: 'fullName',
        isAvailable: true,
      }
    },
    bhUsersQuery() {
      const params = new URLSearchParams()
      for (const [key, value] of Object.entries(this.consultantsQueryParams)) {
        if (value) params.append(key, value)
      }
      return params
    },
    computedConsultants() {
      let consultants = this.consultants.filter(
        consultant => consultant.profileId !== this.bhUser.profileId,
      )
      return consultants.map(consultant => ({
        ...consultant,
        fullName: `${consultant.firstName} ${consultant.lastName}`,
      }))
    },
    isConsultant() {
      return this.dictionaryBhUsersRoleIds[this.bhUser.roleId] === 'Consultant'
    },
    isRoleAllowed() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ==
        this.dictionaryBhUsersRoleIds.Admin
      )
    },
    isPermitted() {
      if (
        this.dictionaryBhUsersRoleIds[this.getRolename] ==
          this.dictionaryBhUsersRoleIds.Admin &&
        this.bhUser.profileId != this.profileId
      )
        return true
      else return false
    },
    roleAllowedToManageAccount() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    fields() {
      return [
        {
          name: 'firstName',
          label: 'First name',
          component: 'bh-text-input',
          disabled: !this.isRoleAllowed,
        },
        {
          name: 'lastName',
          label: 'Last name',
          component: 'bh-text-input',
          disabled: !this.isRoleAllowed,
        },
        {
          name: 'roleId',
          label: 'Role',
          component: 'bh-select',
          disabled: !this.isRoleAllowed,
          items: [
            {
              text: 'Admin',
              value: this.dictionaryBhUsersRoleIds.Admin,
            },
            {
              text: 'Consultant',
              value: this.dictionaryBhUsersRoleIds.Consultant,
            },
            // {
            //   text: 'Technical Support',
            //   value: this.dictionaryBhUsersRoleIds.TechnicalSupport,
            // },
          ],
        },
        {
          name: 'phone',
          label: 'Mobile number',
          component: 'bh-text-input',
          type: 'tel',
          disabled: !this.isRoleAllowed,
        },
        {
          name: 'capacity',
          label: 'Capacity',
          component: 'bh-text-input',
          type: 'number',
          minNumber: 0,
          maxNumber: 100,
          condition:
            this.dictionaryBhUsersRoleIds.Consultant == this.bhUser.roleId,
          disabled: !this.isRoleAllowed,
        },
        {
          name: 'email',
          label: 'Email address',
          type: 'email',
          component: 'bh-text-input',
          confirmationPopup:
            'Are you sure? This email address is also used as a login.',
          validate: true,
          validationType: 'email',
          disabled: !this.isRoleAllowed,
        },
      ]
    },
  },
  methods: {
    ...mapActions('bhUsersStates', [
      'changeBhUserField',
      'getBhUser',
      'getBhUsers',
    ]),
    ...mapActions('patientsStates', ['getPatient', 'changePatientField']),
    ...mapActions('userStates', [
      'sendNewUserEmail',
      'toggleActivationAccount',
      'deleteUser',
    ]),
    inputUpdate(key, value) {
      this.changeBhUserField({ key, value, id: this.userId })
    },
    promptActivateState() {
      if (this.isConsultant) {
        this.deactivationDialogReloader++
        this.showDeactivationDialog = true
      } else {
        this.confirmationPopupActiveStatus = true
      }
    },
    promptDeleteUser() {
      this.confirmationPopupDelete = true
    },
    toggleActiveState() {
      this.toggleActivationAccount({
        url: `/bhUsers/${this.bhUser.id}/active`,
        isActive: !this.bhUser.isActive,
        version: this.bhUser.version,
      }).then(() => {
        this.getBhUser(this.bhUser.id)
      })
    },
    deleteBhUser() {
      if (!this.bhUser.isActive) {
        this.deleteUser({
          url: `/bhUsers/${this.bhUser.id}`,
        })
          .then(() => {
            this.$router.push({ name: 'BhUSers', path: '/bhusers/' })
          })
          .finally(() => {
            this.confirmationPopupDelete = false
          })
      } else {
        this.confirmationPopupDelete = false
      }
    },
    phoneUsFormat: phoneFormat.phoneUsFormat,
    inputPhoneUpdate() {
      let x = this.phoneUsFormat(this.bhUser.phone)
      if (x) this.bhUser.phone = x
      this.inputUpdate('phone', this.bhUser.phone)
    },
    prepareSendNewUserEmail() {
      this.sendNewUserEmail({ profileId: this.bhUser.profileId })
    },
    closeBhUserDeactivationDialog() {
      this.showDeactivationDialog = false
    },
    async changeConsultant(participantId, consultant) {
      await this.getPatient(participantId)
      this.changePatientField({
        key: 'consultantId',
        value: consultant.value,
        id: participantId,
      }).then(() => {
        this.deactivationDialogReloader++
      })
    },
    closeConfirmationActive() {
      this.confirmationPopupActiveStatus = false
    },
    closeConfirmationWelcome() {
      this.confirmationPopupWelcomeMessage = false
    },
    onClickOutsideConfirmationActive() {
      this.closeConfirmationActive()
    },
    onKeydownConfirmationActive(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeConfirmationActive()
    },
    onClickOutsideWelcome() {
      this.closeConfirmationWelcome()
    },
    onKeydownWelcome(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.closeConfirmationWelcome()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.bh-user-general {
  padding: 32px;
  &__tabs {
    flex: initial;
    width: auto;
    padding: 4px;
    border: 1px solid $bh-blue--4;

    &--wrap {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .v-tab {
      color: $bh-blue;
      font-weight: 700;

      &--active {
        background: $bh-blue;
        color: $bh-white !important;
      }
    }
  }

  &--wrap {
    display: flex;
  }

  &-sidebar {
    width: 272px;
    padding: 24px;

    textarea:not([readonly]) {
      padding: 4px;
    }
  }

  &-field {
    margin-bottom: $gutter;
    // example how we can style component (if needed)
    //   &-bh-select {
    //     ::v-deep .bh-select-label{
    //       font-weight: 700;
    //       line-height: 1.425rem;
    //       color: $bh-gray--2;
    //       margin-bottom: 3px;
    //     }
    //   }
  }
  .v-btn {
    margin-left: 10px;
  }
  label.bh-text-input__label {
    font-weight: 700;
    line-height: 1.425rem;
    color: $bh-gray--2;
    margin-bottom: 3px;
  }
}
</style>
